import * as Sentry from '@sentry/react';

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
  /Loading chunk \d+ failed/,
];

export const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(message));
};

const CHUNK_RELOAD_KEY = 'chunk-reload-attempts';
const MAX_CHUNK_RELOADS = 2;

const getChunkReloadCount = (): number =>
  Number.parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0', 10) || 0;

export const attemptChunkReload = (): boolean => {
  try {
    const reloadCount = getChunkReloadCount();
    if (reloadCount >= MAX_CHUNK_RELOADS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(reloadCount + 1));
  } catch {
    return false;
  }
  return true;
};

const CHUNK_REFRESHING_KEY = 'chunk-refreshing';
const CHUNK_REFRESHING_MAX_AGE_MS = 10000;

export const setChunkRefreshingFlag = (): void => {
  try {
    sessionStorage.setItem(CHUNK_REFRESHING_KEY, Date.now().toString());
  } catch {
    Sentry.addBreadcrumb({
      category: 'chunk-load',
      message:
        'sessionStorage inaccessible — chunk-refreshing flag could not be set',
      level: 'warning',
    });
  }
};

export const hasRecentChunkRefreshingFlag = (): boolean => {
  try {
    const chunkRefreshingAt = Number.parseInt(
      sessionStorage.getItem(CHUNK_REFRESHING_KEY) ?? '0',
      10,
    );
    return (
      !!chunkRefreshingAt &&
      Date.now() - chunkRefreshingAt < CHUNK_REFRESHING_MAX_AGE_MS
    );
  } catch {
    Sentry.addBreadcrumb({
      category: 'chunk-load',
      message:
        'sessionStorage inaccessible — chunk-refreshing flag could not be checked',
      level: 'warning',
    });
    return false;
  }
};

export const resetChunkReloadCount = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    Sentry.addBreadcrumb({
      category: 'chunk-load',
      message:
        'sessionStorage inaccessible — chunk-reload count could not be reset',
      level: 'warning',
    });
  }
};
export const importWithRetry = async <T>(
  importFn: () => Promise<T>,
): Promise<T> => {
  try {
    return await importFn();
  } catch {
    Sentry.addBreadcrumb({
      category: 'chunk-load',
      message: 'Dynamic import failed, retrying once',
      level: 'warning',
    });
    try {
      return await importFn();
    } catch (error) {
      Sentry.addBreadcrumb({
        category: 'chunk-load',
        message: 'Retry failed — passing to ErrorBoundary',
        level: 'error',
      });
      throw error;
    }
  }
};

export const confirmChunkRecovery = (): void => {
  resetChunkReloadCount();
  try {
    sessionStorage.removeItem(CHUNK_REFRESHING_KEY);
  } catch {
    Sentry.addBreadcrumb({
      category: 'chunk-load',
      message:
        'sessionStorage inaccessible — chunk-refreshing flag could not be cleared',
      level: 'warning',
    });
  }
};

type Preloadable = {preload: () => Promise<unknown>};

export const preloadAll = (components: readonly Preloadable[]): void => {
  components.forEach(component => {
    component.preload().catch((error: unknown) => {
      if (!isChunkLoadError(error)) Sentry.captureException(error);
    });
  });
};
